import React, {useEffect, useMemo, useRef, useState} from 'react';
import { isSlaEnabled } from '../../utils/slaEnabled';
import AssetsTable from "../../components/AssetsTable";
import {GridPaginationModel, useGridApiRef} from "@mui/x-data-grid-pro";
import ManageColumnsButton from "../../components/ManageColumnsButton";
import {SearchAssetTdas} from "../../graphql/queries/searchAssetTdas";
import {addToUrlParams} from "@dreebit-registry/vsm-microservice-components";
import _ from "lodash";
import ListWithFilterDrawer from '../../components/ListWithFilterDrawer';
import {useSyncedListParams} from "../../hooks/useSyncedListParams";
import AssetExportButton from "../../components/AssetExportButton";
import {TypeDefinitionAttribute} from "../../types";
import {useTranslation} from "react-i18next";
import {SearchAssetQueryField} from "../../codegen/graphql";
import {resolveValidQueryFields} from "../../utils/resolveValidQueryFields";
import {parseAssetListSortParam, toAssetListSort} from "../../utils/assetListSort";

interface Props {
    onOpenAsset: (assetId: string) => void;
}

// Default selected search fields are sourced from the TDA search attribute
// (searchAttribute.addOnAttribute.value), so no fields are hardcoded here.
export const defaultQueryFields: string[] = []

const METRIC_FILTER_MAP: Record<string, { staticFilter: Record<string, any>; temporaryColumns: string[]; chipLabel: string }> = {
    maintenanceDueSoon: {
        staticFilter: { assetMetrics: { maintenanceDueSoon: { equals: true } } },
        temporaryColumns: [
            'maintenance',
            'lastMaintenanceDate', 'lastExhaustFilterChangeDate',
            'lastOilAndFilterChangeDate', 'lastRemanufacturingDate',
            'daysUntilNextExhaustFilterChange', 'daysUntilNextOilAndFilterChange',
            'daysUntilNextRemanufacturing', 'daysUntilNextService',
        ],
        chipLabel: 'Maintenance Due',
    },
    maintenanceOverdue: {
        staticFilter: { assetMetrics: { maintenanceOverdue: { equals: true } } },
        temporaryColumns: [
            'maintenance',
            'lastMaintenanceDate', 'lastExhaustFilterChangeDate',
            'lastOilAndFilterChangeDate', 'lastRemanufacturingDate',
            'daysUntilNextExhaustFilterChange', 'daysUntilNextOilAndFilterChange',
            'daysUntilNextRemanufacturing', 'daysUntilNextService',
        ],
        chipLabel: 'Maintenance Overdue',
    },
    harshCondition: {
        staticFilter: { assetMetrics: { harshCondition: { in: ["GOOD", "MEDIUM", "POOR"] } } },
        temporaryColumns: ['harshCondition', 'harshConditionScore'],
        chipLabel: 'Harsh Condition',
    },
    exhaustFilter: {
        staticFilter: { assetMetrics: { exhaustFilterCondition: { in: ["GOOD", "MEDIUM", "POOR"] } } },
        temporaryColumns: ['exhaustFilter', 'exhaustFilterConditionScore'],
        chipLabel: 'Exhaust Filter',
    },
    missingSensor: {
        staticFilter: { assetMetrics: { missingSensorValues: { equals: true } } },
        temporaryColumns: ['missingSensor', 'lastMissingSensorValuesAt'],
        chipLabel: 'Missing Sensor',
    },
    vsdCandidate: {
        staticFilter: { assetMetrics: { vsdCandidate: { in: ["GOOD", "MEDIUM", "POOR"] } } },
        temporaryColumns: ['vsdCandidate', 'vsdCandidateScore'],
        chipLabel: 'VSD Candidate',
    },
    sensorValueOutOfRange: {
        staticFilter: { assetMetrics: { sensorValueOutOfRange: { equals: true } } },
        temporaryColumns: [
            'sensorValueOutOfRange', 'lastSensorValueOutOfRangeAt',
            'sensorValueOutOfRangeParameter', 'sensorValueOutOfRangeRecordedValue',
            'sensorValueOutOfRangeLowerThreshold', 'sensorValueOutOfRangeUpperThreshold',
        ],
        chipLabel: 'Sensor Value out of Range',
    },
};

const AssetListScreen = ({onOpenAsset}: Props) => {
    const {t} = useTranslation();
    const apiRef = useGridApiRef();
    const manageColumnsButtonRef = useRef<HTMLButtonElement>(null);
    const [viewportHeight, setViewportHeight] = useState(window.innerHeight);
    const [attributes, setAttributes] = useState<TypeDefinitionAttribute[]>([]);

    const showEnvBanner = process.env.REACT_APP_SHOW_TESTING_BANNER === "true"
    const tableHeight =
        viewportHeight
        - (showEnvBanner ? 40 : 0) // Test Env Banner
        - 56 // Filter SearchBar
        - 63 // header
        - 50 // Tabs
        - 16; // content padding

    // One authority for the search field whitelist: the backend search TDA. The generated enum is
    // only the bootstrap fallback until `onAttributesChange` has delivered the loaded TDAs.
    const validQueryFields = useMemo(
        () => resolveValidQueryFields(attributes, Object.values(SearchAssetQueryField)),
        [attributes]
    );

    const {
        urlParams,
        setUrlParams,
        searchParams,
        setSearchParams,
        filterParams,
        setFilterParams,
        resetFilterParams
    } = useSyncedListParams({
        storagePrefix: "assetList",
        defaultQueryFields: defaultQueryFields,
        validQueryFields: validQueryFields,
        searchParamsVersion: 4, // bump to force a one-time reset to TDA default search fields
        urlKeys: { nonFilterKeys: ["page", "pageSize", "sort"] },
    });
    const sort = toAssetListSort(parseAssetListSortParam(urlParams.get("sort")));

    // Derive static filter + temporary columns from any active metric filter param
    const activeMetricKey = Object.keys(METRIC_FILTER_MAP).find(k => filterParams[k]);
    const metricConfig = activeMetricKey ? METRIC_FILTER_MAP[activeMetricKey] : undefined;

    // Jump-off from a node's "Network scan" tab (IBM-1039): `?installedBaseNodeId=<id>`
    // restricts the list to the assets below that node. The key has no filter TDA,
    // so it only reaches the request as a static filter (the backend matches any
    // node on the asset's parent path); the drawer still renders it as a chip.
    const installedBaseNodeId = isSlaEnabled() && typeof filterParams.installedBaseNodeId === 'string'
        ? filterParams.installedBaseNodeId
        : undefined;
    const staticFilters = metricConfig?.staticFilter || installedBaseNodeId
        ? {
            ...(metricConfig?.staticFilter ?? {}),
            ...(installedBaseNodeId ? { installedBaseNodeId: { equals: installedBaseNodeId } } : {}),
        }
        : undefined;

    const chipLabelOverrides: Record<string, string> = {
        geolocation: t('Location'),
        installedBaseNodeId: t('Installed base element'),
        ...Object.fromEntries(Object.entries(METRIC_FILTER_MAP).map(([k, v]) => [k, t(v.chipLabel)])),
    };

    // The debounced handler must survive re-renders, otherwise a parent re-render mid-typing
    // orphans the pending timer, which then fires alongside the new one with a stale snapshot —
    // two contradictory searches. `urlParams`/`setUrlParams` are read through refs because
    // react-router recreates both on every URL change, so they cannot be memo dependencies.
    const urlParamsRef = useRef(urlParams);
    urlParamsRef.current = urlParams;
    const setUrlParamsRef = useRef(setUrlParams);
    setUrlParamsRef.current = setUrlParams;

    const onDebouncedSearch = useMemo(
        () => _.debounce((values: { query: string; fields: string[] }) => {
            setSearchParams(values);
            addToUrlParams(urlParamsRef.current, values, setUrlParamsRef.current);
        }, 700),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    useEffect(() => () => onDebouncedSearch.cancel(), [onDebouncedSearch]);

    useEffect(() => {
        const handleResize = () => {
            setViewportHeight(window.innerHeight);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return (
        <ListWithFilterDrawer
            query={SearchAssetTdas}
            attributesPath="searchAssets.filterTypeDefinitionAttributeList.typeDefinitionAttributes"
            urlParams={urlParams}
            setUrlParams={setUrlParams}
            searchParams={searchParams}
            filterParams={filterParams}
            setFilterParams={setFilterParams}
            resetFilterParams={resetFilterParams}
            onSearch={onDebouncedSearch}
            openStateStorageKey="assetList_filterDrawerOpen"
            onAttributesChange={setAttributes}
            chipLabelOverrides={chipLabelOverrides}
            extraHeaderActions={
                <>
                    <ManageColumnsButton apiRef={apiRef} buttonRef={manageColumnsButtonRef}/>
                    <AssetExportButton
                        filter={filterParams}
                        searchParams={searchParams}
                        attributes={attributes}
                        staticFilters={staticFilters}
                        apiRef={apiRef}
                        sort={sort}
                    />
                </>
            }
        >
            <AssetsTable
                height={tableHeight}
                searchParams={searchParams}
                urlParams={urlParams}
                filter={filterParams}
                onChangePagination={(newPaginationModel: GridPaginationModel) => {
                    if (newPaginationModel) {
                        addToUrlParams(urlParams, newPaginationModel, setUrlParams);
                    }
                }}
                onChangeSort={(value?: string) => {
                    addToUrlParams(urlParams, { sort: value ?? [] }, setUrlParams);
                }}
                onOpenAsset={onOpenAsset}
                staticFilters={staticFilters}
                temporaryVisibleColumns={metricConfig?.temporaryColumns}
                apiRef={apiRef}
                panelAnchorRef={manageColumnsButtonRef}
            />
        </ListWithFilterDrawer>
    );
};

export default AssetListScreen;


